import React from 'react';
import PropTypes from 'prop-types';
import { LuBan, LuChevronDown, LuCircleCheck, LuHourglass, LuLoaderCircle, LuWrench } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import { STATUSES } from '../errorReportColumns';
import useErrorReportText from '../errorReportText';

/** Look of each stored status: badge tone, icon, avatar gradient, icon / select colours. */
export const STATUS_META = {
  Beklemede: {
    tone: 'warning',
    icon: LuHourglass,
    gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30',
    text: 'tw-text-amber-700 dark:tw-text-amber-200',
    select: 'tw-border-amber-500/30 tw-bg-amber-50 tw-text-amber-800 dark:tw-border-amber-400/30 dark:tw-bg-amber-400/10 dark:tw-text-amber-200',
  },
  'İşlemde': {
    tone: 'info',
    icon: LuWrench,
    gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30',
    text: 'tw-text-blue-700 dark:tw-text-blue-200',
    select: 'tw-border-blue-500/30 tw-bg-blue-50 tw-text-blue-800 dark:tw-border-blue-400/30 dark:tw-bg-blue-400/10 dark:tw-text-blue-200',
  },
  'Tamamlandı': {
    tone: 'success',
    icon: LuCircleCheck,
    gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30',
    text: 'tw-text-emerald-700 dark:tw-text-emerald-200',
    select: 'tw-border-emerald-500/30 tw-bg-emerald-50 tw-text-emerald-800 dark:tw-border-emerald-400/30 dark:tw-bg-emerald-400/10 dark:tw-text-emerald-200',
  },
  'İptal': {
    tone: 'neutral',
    icon: LuBan,
    gradient: 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30',
    text: 'tw-text-slate-600 dark:tw-text-slate-300',
    select: 'tw-border-slate-300 tw-bg-slate-100 tw-text-slate-700 dark:tw-border-white/15 dark:tw-bg-white/[0.06] dark:tw-text-slate-200',
  },
};

export const statusMeta = (status) => STATUS_META[status] || STATUS_META.Beklemede;

/** Two letters of a name ("Ayşe Kaya" → "AK"). */
export const initialsOf = (name) => {
  const parts = String(name || '').replace(/\(.*\)/, '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.length === 1 ? parts[0].slice(0, 2).toLocaleUpperCase('tr') : `${parts[0][0]}${parts[parts.length - 1][0]}`.toLocaleUpperCase('tr');
};

export const StatusBadge = ({ status }) => {
  const { statusLabel } = useErrorReportText();
  const meta = statusMeta(status);
  const Icon = meta.icon;
  return (
    <Badge tone={meta.tone}>
      <Icon aria-hidden="true" className="tw-h-3 tw-w-3" />
      {statusLabel(status)}
    </Badge>
  );
};

StatusBadge.propTypes = { status: PropTypes.string };

/**
 * Status picker inside a table row (as the old row dropdown). Its clicks and
 * keys stay inside, so using it does not select the row.
 */
export const StatusSelect = ({ status, busy, onChange }) => {
  const { label, statusLabel } = useErrorReportText();
  const meta = statusMeta(status);
  const Icon = busy ? LuLoaderCircle : meta.icon;
  const stop = (event) => event.stopPropagation();
  return (
    <span className="tw-relative tw-inline-flex tw-max-w-full" onClick={stop} onKeyDown={stop} role="presentation">
      <Icon aria-hidden="true" className={`tw-pointer-events-none tw-absolute tw-left-2.5 tw-top-1/2 tw-h-3.5 tw-w-3.5 -tw-translate-y-1/2 ${busy ? 'tw-animate-spin' : ''} ${meta.text}`} />
      <select
        value={status}
        disabled={busy}
        aria-label={label('table.changeStatus')}
        onChange={(event) => onChange(event.target.value)}
        className={`tw-h-8 tw-max-w-full tw-cursor-pointer tw-appearance-none tw-truncate tw-rounded-full tw-border tw-py-0 tw-pl-7 tw-pr-7 tw-text-xs tw-font-semibold tw-shadow-none tw-outline-none tw-transition-colors focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 disabled:tw-cursor-wait disabled:tw-opacity-70 ${meta.select}`}
      >
        {STATUSES.map((value) => <option key={value} value={value}>{statusLabel(value)}</option>)}
      </select>
      <LuChevronDown aria-hidden="true" className={`tw-pointer-events-none tw-absolute tw-right-2.5 tw-top-1/2 tw-h-3.5 tw-w-3.5 -tw-translate-y-1/2 tw-opacity-70 ${meta.text}`} />
    </span>
  );
};

StatusSelect.propTypes = { status: PropTypes.string.isRequired, busy: PropTypes.bool, onChange: PropTypes.func.isRequired };

/** The four statuses as buttons (selected record's panel). */
export const StatusChooser = ({ status, busy, onChange }) => {
  const { statusLabel } = useErrorReportText();
  return (
    <div className="tw-grid tw-grid-cols-2 tw-gap-2">
      {STATUSES.map((value) => {
        const meta = statusMeta(value);
        const Icon = meta.icon;
        const active = value === status;
        return (
          <button
            key={value}
            type="button"
            aria-pressed={active}
            disabled={busy || active}
            onClick={() => onChange(value)}
            className={`tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-px-3 tw-py-2 tw-text-left tw-text-[13px] tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 disabled:tw-cursor-default ${
              active ? meta.select : 'tw-border-slate-200 tw-bg-white/70 tw-text-slate-600 hover:tw-bg-white hover:tw-text-slate-900 disabled:tw-opacity-60 dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:tw-text-slate-300 dark:hover:tw-bg-white/[0.07] dark:hover:tw-text-white'
            }`}
          >
            <Icon aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0" />
            <span className="tw-min-w-0 tw-truncate">{statusLabel(value)}</span>
          </button>
        );
      })}
    </div>
  );
};

StatusChooser.propTypes = { status: PropTypes.string.isRequired, busy: PropTypes.bool, onChange: PropTypes.func.isRequired };
